/**
 * HTML Template Preview Panel
 * Renders a live preview of the HTML template with interpolated data in a sandboxed iframe.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface HtmlTemplatePreviewPanelProps {
  template: string;
  primaryColor: string;
  customCss: string;
  inputData: InputData[];
}

// Base styles matching the backend HtmlTemplateNode.GetBaseStyles()
function getBaseStyles(primaryColor: string): string {
  return `* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px; }
h1 { color: ${primaryColor}; margin-bottom: 10px; }
h2 { color: #555; font-weight: normal; margin-bottom: 20px; }
h3 { color: #444; margin-bottom: 10px; }
h4 { color: #555; margin-bottom: 8px; }
p { margin-bottom: 8px; }
ul { margin-left: 20px; margin-bottom: 15px; }
li { margin-bottom: 5px; }
pre { background: #f5f5f5; padding: 15px; border-radius: 4px; overflow-x: auto; }
code { font-family: monospace; }
.data-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
.data-table th, .data-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
.data-table th { background: ${primaryColor}; color: white; }
.data-table tr:nth-child(even) { background: #f9f9f9; }
.timestamp { color: #666; font-size: 0.9em; margin-bottom: 20px; }
.report-header, .report-footer { padding: 10px 0; border-bottom: 1px solid #ddd; color: #666; }
.report-footer { border-top: 1px solid #ddd; border-bottom: none; margin-top: 30px; }
.invoice { max-width: 800px; }
.invoice-header { border-bottom: 2px solid ${primaryColor}; padding-bottom: 20px; margin-bottom: 20px; }
.invoice-items { width: 100%; border-collapse: collapse; margin: 20px 0; }
.invoice-items th, .invoice-items td { border: 1px solid #ddd; padding: 10px; text-align: left; }
.invoice-items th { background: ${primaryColor}; color: white; }
.invoice-items tfoot td { border-top: 2px solid #ddd; }
.total-row { background: #f5f5f5; font-size: 1.1em; }
.bill-to { margin-bottom: 30px; }
.invoice-notes { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; }
.receipt { max-width: 400px; margin: 0 auto; text-align: center; }
.receipt-header { margin-bottom: 20px; }
.receipt-header h1 { font-size: 1.5em; }
.receipt-header h2 { font-size: 1.2em; color: #333; }
.receipt-items { width: 100%; margin: 15px 0; text-align: left; }
.receipt-items th, .receipt-items td { padding: 8px; border-bottom: 1px solid #eee; }
.receipt-totals { text-align: right; margin: 20px 0; padding-top: 15px; border-top: 2px solid #333; }
.receipt-totals .total { font-size: 1.2em; margin-top: 10px; }
.thank-you { margin-top: 30px; color: #666; font-style: italic; }
.certificate { text-align: center; padding: 20px; }
.certificate-border { border: 3px solid ${primaryColor}; padding: 40px; background: linear-gradient(to bottom, #fefefe, #f5f5f5); }
.certificate-content { border: 1px solid #ddd; padding: 40px; background: white; }
.certificate-title { color: ${primaryColor}; font-size: 2.5em; margin-bottom: 30px; font-family: Georgia, serif; }
.recipient-name { color: #333; font-size: 2em; margin: 20px 0 30px; font-family: Georgia, serif; border-bottom: 2px solid ${primaryColor}; display: inline-block; padding-bottom: 10px; }
.summary-document { max-width: 900px; }
.summary-header { margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid ${primaryColor}; }
.metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 20px; margin: 30px 0; }
.metric-card { background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center; border-left: 4px solid ${primaryColor}; }
.metric-value { font-size: 2em; font-weight: bold; color: ${primaryColor}; }
.metric-label { color: #666; font-size: 0.9em; text-transform: uppercase; letter-spacing: 1px; }
.data-section { margin-top: 30px; }`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Render input data as an HTML table, matching the backend RenderDataAsTable() */
function renderDataAsTable(data: any): string {
  if (data == null) return '<p>No data</p>';

  // Normalize to array
  let items: any[];
  if (Array.isArray(data)) {
    items = data;
  } else if (typeof data === 'object') {
    items = [data];
  } else {
    return `<p>${escapeHtml(String(data))}</p>`;
  }

  if (items.length === 0) return '<p>No data</p>';

  // Extract columns from first item
  const first = items[0];
  if (first == null || typeof first !== 'object') {
    return `<pre><code>${escapeHtml(JSON.stringify(data, null, 2))}</code></pre>`;
  }

  const columns = Object.keys(first);
  if (columns.length === 0) {
    return `<pre><code>${escapeHtml(JSON.stringify(data, null, 2))}</code></pre>`;
  }

  let html = '<table class="data-table">\n<thead><tr>\n';
  for (const col of columns) {
    html += `<th>${escapeHtml(col)}</th>\n`;
  }
  html += '</tr></thead>\n<tbody>\n';

  for (const item of items) {
    html += '<tr>\n';
    for (const col of columns) {
      const val = item?.[col];
      let display: string;
      if (val == null) {
        display = '';
      } else if (typeof val === 'object') {
        if (Array.isArray(val)) {
          display = `[${val.length} items]`;
        } else {
          display = '[object]';
        }
      } else {
        display = String(val);
      }
      html += `<td>${escapeHtml(display)}</td>\n`;
    }
    html += '</tr>\n';
  }
  html += '</tbody></table>';
  return html;
}

/** Resolve a dotted path like "output.field.subfield" against an object */
function resolvePath(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return current;
}

/** Format a value for display in the template */
function formatValue(val: any): string {
  if (val == null) return '';
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }
  return String(val);
}

/**
 * Interpolate the template with available data, matching backend logic:
 * - {{ $timestamp }} → current UTC time
 * - {{ $input }} → render all input data as a table
 * - {{ $node["NodeName"].output.field }} → resolve from inputData
 */
function interpolateTemplate(
  template: string,
  inputData: InputData[]
): { html: string; errors: string[] } {
  const errors: string[] = [];
  let result = template;

  // 1. Replace {{ $input }} with table of first connected input's data
  if (result.includes('{{ $input }}') || result.includes('{{$input}}')) {
    const firstInput = inputData.length > 0 ? inputData[0] : null;
    const inputOutput = firstInput?.output;
    // Try to get the inner output (matching backend behavior with unified format)
    const actualData = inputOutput?.output ?? inputOutput;
    const tableHtml = renderDataAsTable(actualData);
    result = result.replace(/\{\{\s*\$input\s*\}\}/g, tableHtml);
  }

  // 2. Replace {{ $timestamp }}
  const timestamp = new Date().toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
  result = result.replace(/\{\{\s*\$timestamp\s*\}\}/g, timestamp);

  // 3. Replace {{ $node["NodeName"].output.field }} expressions
  // Build a lookup map: nodeName/nodeLabel → output data
  const nodeOutputMap = new Map<string, any>();
  for (const input of inputData) {
    const output = input.output?.output ?? input.output;
    nodeOutputMap.set(input.nodeName, output);
  }

  result = result.replace(/\{\{\s*(.+?)\s*\}\}/g, (match, expression: string) => {
    // Parse $node["NodeName"].output.field
    const nodeMatch = expression.match(/^\$node\["([^"]+)"\]\.(.+)$/);
    if (!nodeMatch) {
      // Unknown expression — leave as-is so user sees it
      return match;
    }

    const [, nodeName, path] = nodeMatch;
    const nodeOutput = nodeOutputMap.get(nodeName);
    if (nodeOutput === undefined) {
      // Node not found in input data — show placeholder
      return `<span style="color:#999;font-style:italic">[${nodeName}: no data]</span>`;
    }

    const value = resolvePath({ output: nodeOutput }, path);
    if (value === undefined) {
      return `<span style="color:#999;font-style:italic">[${nodeName}.${path}: not found]</span>`;
    }

    return escapeHtml(formatValue(value));
  });

  return { html: result, errors };
}

/** Wrap content with full HTML structure if not already a complete document */
function wrapWithHtmlStructure(content: string, primaryColor: string, customCss: string): string {
  const trimmed = content.trimStart();
  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
    return content;
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    ${getBaseStyles(primaryColor)}
    ${customCss}
  </style>
</head>
<body>
  ${content}
</body>
</html>`;
}

export function HtmlTemplatePreviewPanel({
  template,
  primaryColor,
  customCss,
  inputData,
}: HtmlTemplatePreviewPanelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Compute the rendered HTML
  const renderedHtml = useMemo(() => {
    if (!template || !template.trim()) {
      return null; // blank template
    }

    try {
      const { html, errors } = interpolateTemplate(template, inputData);
      if (errors.length > 0) {
        setRenderError(errors.join('; '));
      } else {
        setRenderError(null);
      }
      return wrapWithHtmlStructure(html, primaryColor, customCss);
    } catch (err: any) {
      setRenderError(err.message || 'Failed to render template');
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, primaryColor, customCss, inputData, refreshKey]);

  // Write to iframe via srcdoc whenever renderedHtml changes
  useEffect(() => {
    if (iframeRef.current && renderedHtml) {
      iframeRef.current.srcdoc = renderedHtml;
    }
  }, [renderedHtml]);

  // Blank template state
  if (!template || !template.trim()) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Eye className="w-10 h-10 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">
          Enter an HTML template in the Parameters tab to see a preview here.
        </p>
      </div>
    );
  }

  // Error state
  if (renderError && !renderedHtml) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
          <span className="text-sm font-medium">Preview</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-7 w-7 p-0">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 p-8">
          <AlertTriangle className="w-8 h-8 text-destructive mb-3" />
          <p className="text-sm font-medium text-destructive mb-1">Invalid HTML Template</p>
          <p className="text-xs text-muted-foreground text-center">{renderError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Preview</span>
          {renderError && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Partial render
            </span>
          )}
          {inputData.length === 0 && (
            <span className="text-xs text-muted-foreground">
              (no input data — run upstream nodes first)
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-7 w-7 p-0" title="Refresh preview">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* iframe preview */}
      <div className="flex-1 min-h-0 bg-white overflow-hidden">
        <iframe
          ref={iframeRef}
          sandbox="allow-same-origin"
          title="HTML Template Preview"
          className="w-full h-full border-0"
          srcDoc={renderedHtml || ''}
        />
      </div>
    </div>
  );
}
